import { FC, ReactElement, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Editor } from 'react-draft-wysiwyg';
import { Form, Field } from 'react-final-form';
import moment from 'moment';
import { format, parseISO } from 'date-fns';
import { convertToRaw } from 'draft-js';

import { Box, Button, Loader, RFFTextInput } from '@qwealth/qcore';
import { dateFormat, QNote, QTask, selectUser } from '@qwealth/qdata';
import { commonSubscription, DocumentUploader, InputForm } from '@qwealth/kyc';

import KYCFlags from 'components/common/KYCFlags';
import { buildAllMembers, getCheckedMemberQids } from 'data/helpers/meaningfulInteraction';
import { loadHouseholdNotes, saveQNote } from 'data/actions/qnotes';
import qnoteValidator from 'data/validations/qnote';
import { useInitializedEditorState } from 'hooks/useInitializedEditorState';
import { EditorWrapper } from 'components/common/EditorWrapper';
import { MeaningfulContent } from 'components/common/MeaningfulContent';
import TransitionBox from '../TransitionBox';
import QNoteAuthorData from './QNoteAuthorData';
import WealthboxWarningMessage from './WealthboxWarningMessage';
import QNoteVersionSelector from './QNoteVersionSelector';
import { useAppDispatch } from 'data/store';
import { getQnoteAttachmentPath } from 'data/helpers/qnoteFilterHelper';
import { selectClientMembers, selectHouseholdLegalEntities } from '../../../../../data/selectors/householdSelectors';
import { QNoteFormValues } from 'data/models/qnoteForm';

interface QNoteFormProps {
  note?: QNote | null;
  noteVersions?: QNote[];
  hasWealthboxConflict?: boolean;
  householdQID: string;
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
  onClose: () => void;
  setIsTaskModalOpen?: (isOpen: boolean) => void;
  task?: Partial<QTask> | null;
  isQnoteModalFromTask?: boolean;
}

export const QNoteForm: FC<QNoteFormProps> = ({
  note,
  noteVersions,
  hasWealthboxConflict,
  householdQID,
  isLoading,
  setIsLoading,
  onClose,
  setIsTaskModalOpen,
  task,
  isQnoteModalFromTask,
}): ReactElement => {
  const user = useSelector(selectUser);
  const clientMembers = useSelector(selectClientMembers);
  const legalEntities = useSelector(selectHouseholdLegalEntities);

  const [editorState, setEditorState] = useInitializedEditorState(note);
  const [displayedNote, setDisplayedNote] = useState<QNote>();
  const [qNoteAttachmentsPath, setQnoteAttachmentsPath] = useState<string>('');

  useEffect(() => {
    changeDisplayedNote(note);
  }, []);

  const changeDisplayedNote = (noteToDisplay: QNote | undefined | null) => {
    setDisplayedNote({
      ...noteToDisplay,
      owner: noteToDisplay?.owner ?? [],
      title: noteToDisplay?.title ?? '',
      author: noteToDisplay?.author ?? `${user.firstName} ${user.lastName}`,
      date: noteToDisplay?.date ?? moment().format(dateFormat),
      content: noteToDisplay?.content ?? '',
      isSignificantChange: noteToDisplay?.isSignificantChange || 0,
      isMeaningfulInteraction: noteToDisplay?.isMeaningfulInteraction || 0,
      isPortfolioReview: noteToDisplay?.isPortfolioReview || 0,
    } as QNote);
  };

  useEffect(() => {
    if (displayedNote && (displayedNote.uuid || displayedNote.historicId) && householdQID) {
      const noteId = displayedNote.historicId || displayedNote.uuid;
      if (noteId) {
        const path = getQnoteAttachmentPath(householdQID, noteId);
        setQnoteAttachmentsPath(path);
      }
    }
  }, [displayedNote, householdQID]);

  const dispatch = useAppDispatch();

  const submitNote = (values: QNoteFormValues) => {
    (async () => {
      const { relatedMembersChecked, selectAllRelatedMembers, ...rest } = values;
      const relatedMembers = getCheckedMemberQids(allMembers, relatedMembersChecked);

      setIsLoading(true);
      await dispatch(
        saveQNote({
          ...rest,
          relatedMembers,
          noteContent: JSON.stringify(convertToRaw(editorState.getCurrentContent())),
          householdQID,
        }),
      );
      await dispatch(loadHouseholdNotes(householdQID));
      setIsLoading(false);
      onClose();
    })();
  };

  const allMembers = buildAllMembers(clientMembers, legalEntities);

  return (
    <Box width="100%" display="flex" alignItems="center" justifyContent="center">
      {isLoading ? (
        <Loader />
      ) : (
        <TransitionBox index={1} isVisible={true} width="90%">
          <Box marginTop="large">
            <Form<QNoteFormValues>
              initialValues={displayedNote}
              onSubmit={submitNote}
              validate={values => qnoteValidator(values, editorState)}
            >
              {({ handleSubmit, submitting }) => {
                return (
                  <Box display="flex" flexDirection="column">
                    <Box display="flex" flexDirection="row">
                      <Box marginRight="largest" display="flex" flexDirection="column" gap="large">
                        <Box display="flex" flexDirection="row" gap="largest">
                          <InputForm width="275px">
                            <RFFTextInput name="title" label="Title*" subscription={commonSubscription} />
                          </InputForm>
                        </Box>
                        {displayedNote && (
                          <Box display="flex" flexDirection="column">
                            <Box fontWeight="bold">KYC Flags</Box>
                            <KYCFlags kycFlags={displayedNote} mt="default" mb="default" />
                          </Box>
                        )}
                      </Box>

                      <Box display="flex" flexDirection="column" width="100%">
                        {hasWealthboxConflict && <QNoteVersionSelector qnotes={[note as QNote, note as QNote]} onChange={changeDisplayedNote} />}
                        {displayedNote && (
                          <QNoteAuthorData
                            createdDate={displayedNote.date}
                            createdBy={displayedNote.author}
                            //@ts-ignore ToDo: Check why qnote model in qdata did not update properly
                            updatedDate={displayedNote.lastUpdated ? format(parseISO(displayedNote.lastUpdated), 'yyyy-MM-dd') : ''}
                            updatedBy={displayedNote.lastUpdatedBy}
                            task={task}
                            noteId={displayedNote.uuid}
                            setIsTaskModalOpen={setIsTaskModalOpen}
                            isQnoteModalFromTask={isQnoteModalFromTask}
                          />
                        )}
                        {hasWealthboxConflict && <WealthboxWarningMessage />}
                      </Box>
                    </Box>

                    {/* @ts-ignore TODO: remove after version bump */}
                    <MeaningfulContent allMembers={allMembers} disabledQids={note?.relatedMembers || []} />

                    <Box fontWeight="bold" marginBottom="default" marginTop="large">
                      Content*
                    </Box>
                    <Box>
                      <Field name="content" subscription={commonSubscription}>
                        {({ input, meta }) => (
                          <EditorWrapper hasError={!!meta.error && meta.touched}>
                            <Editor
                              editorState={editorState}
                              handlePastedText={() => false}
                              onEditorStateChange={editorState => {
                                setEditorState(editorState);
                                input.onChange((Math.random() + 1).toString(36).substring(7));
                              }}
                              editorClassName="wysiwygEditor"
                              onBlur={() => {
                                input.onBlur();
                              }}
                              spellCheck
                            />
                            {meta.error && meta.touched && <Box color="error">{meta.error}</Box>}
                          </EditorWrapper>
                        )}
                      </Field>
                    </Box>

                    <Box marginTop="large">
                      <DocumentUploader
                        title="QNotes Attachments"
                        label="QNotes  Attachments"
                        editing
                        isMulti
                        directPath={qNoteAttachmentsPath}
                        documentPreviewPosition="side"
                      />
                    </Box>
                    <Field name="pending" subscription={commonSubscription}>
                      {() => null}
                    </Field>

                    <Box display="flex" justifyContent="end" gap="default" paddingTop="large" marginBottom="large">
                      <Button onClick={handleSubmit} disabled={submitting}>
                        Save
                      </Button>
                      <Button variant="outline" onClick={onClose}>
                        Cancel
                      </Button>
                    </Box>
                  </Box>
                );
              }}
            </Form>
          </Box>
        </TransitionBox>
      )}
    </Box>
  );
};
